<template>
  <!-- this is necessary to prevent the listbox from staying open when used inside a TFormField -->
  <!-- https://github.com/tailwindlabs/headlessui/issues/2843 -->
  <div @click.prevent.stop>
    <Listbox v-model="selected" as="div">
      <div class="relative">
        <ListboxButton
          class="flex w-full cursor-pointer items-center justify-between border-0 pl-3 focus:ring-0"
        >
          <div v-if="selected">
            <slot name="button" :selected="selected">
              <span v-if="selectedLabel" class="block truncate text-base">{{
                selectedLabel
              }}</span>
            </slot>
          </div>
          <span v-else class="text-base text-gray-500">{{ placeholder }}</span>

          <span class="pointer-events-none flex items-center">
            <TIcon name="arrow_drop_down" />
          </span>
        </ListboxButton>
        <transition
          leave-active-class="transition ease-in duration-100"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <!-- Make options as wide as the form container  -->
          <ListboxOptions
            class="focus:ring-none bg-white-500 absolute z-10 mt-4 ml-[-12px] max-h-60 w-[calc(100%+24px)] overflow-auto rounded-md text-sm shadow-lg focus:outline-hidden sm:text-sm"
          >
            <ListboxOption
              v-if="nullable"
              v-slot="{ active, selected: optionSelected }"
              :value="null"
            >
              <li
                :class="[
                  active ? 'bg-primary-100 text-primary-500' : 'text-gray-900',
                  'relative cursor-default py-2 pr-9 pl-3 select-none',
                ]"
              >
                <span
                  :class="[
                    optionSelected ? 'font-semibold' : 'font-normal',
                    'block truncate',
                  ]"
                >
                  -- {{ $t('generic_choose') }} --
                </span>
              </li>
            </ListboxOption>
            <ListboxOption
              v-for="option in options"
              :key="labelForOption(option)"
              v-slot="{ active, selected: optionSelected }"
              as="template"
              :value="option"
            >
              <li
                :class="[
                  active ? 'bg-primary-100 text-primary-500' : 'text-gray-900',
                  'relative cursor-default py-2 pr-9 pl-3 select-none',
                ]"
              >
                <span
                  :class="[
                    optionSelected ? 'font-semibold' : 'font-normal',
                    'block truncate',
                  ]"
                >
                  <slot
                    name="option"
                    :active="active"
                    :selected="optionSelected"
                    :option="option"
                  >
                    {{ labelForOption(option) }}
                  </slot>
                </span>
              </li>
            </ListboxOption>
          </ListboxOptions>
        </transition>
      </div>
    </Listbox>
  </div>
</template>

<script
  setup
  lang="ts"
  generic="
    T extends (Record<string, unknown> & { label: string }) | string | number
  "
>
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/vue';
import { $t } from '@teachino/shared-i18n';
import { computed } from 'vue';
import TIcon from '../Icon/TIcon.vue';

withDefaults(
  defineProps<{
    options: T[];
    placeholder?: string;
    label?: string;
    nullable?: boolean;
  }>(),
  {
    nullable: false,
    placeholder: $t('generic_select_placeholder'),
    label: undefined,
  },
);
const selected = defineModel<T | null>();

function labelForOption(option: T): string {
  if (typeof option === 'string') {
    return option;
  }

  if (typeof option === 'number') {
    return String(option);
  }

  return option.label;
}

const selectedLabel = computed<string | null>(() =>
  selected.value ? labelForOption(selected.value) : null,
);
</script>
